/* 滿寵（字伯寧） */
import { SG, K, gd, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, P, D } from '../define.ts';

export default defineHero({
  id: 'manchong', name: '滿寵', sub: '字伯寧', faction: 'wei', role: '法師', weapon: 'staff',
  look: { head: 'guan', robe: true, beard: 'goatee', eye: 'narrow', fx: '#ff9a4a', c: { cloth: '#4a2a2a', sleeve: '#4a2a2a', pole: '#3a2418' } },
  skills: [
    ['順風縱火', 'fire', '朝方向放出一道火牆向前推進，{81/126/171/216} 傷害並點燃；東風吹起時火牆變寬一倍、走得更遠，順手點燃路上的蘆葦。', { x: { k: 'firewall', dmg: [81, 126, 171, 216], w: 140, speed: 520 }, aim: D(700, 70), cd: 10, mp: [80, 90, 100, 110], el: 'fire' }],
    ['合肥新城', 'summon', '在指定地點立起一道長 {300/340/380/420} 的城牆 4 秒，敵我都過不去、擋得住箭矢，只能繞路；被牆夾到的人會被推到兩側。', { x: { k: 'citywall', len: [300, 340, 380, 420], dur: 4 }, aim: P(650, 60), cd: [22, 20, 18, 16], mp: 100, el: 'earth' }],
    ['嚴刑', 'passive', '對生命低於 40% 的敵人，技能傷害 +{10/15/20/25}%。', { pv: {}, sig: 'harsh', amp: [10, 15, 20, 25] }],
    ['焚械', 'fire', '指定一大塊區域，4 秒內火焰反覆燒灼，敵將每秒受到 {80/120/160} 傷害；攻城器械（衝車、投石車）與召喚物受到三倍傷害。', { x: { k: 'burnsiege', r: 300, dur: 4, dps: [80, 120, 160] }, aim: P(650, 300), cd: [100, 85, 70], mp: [150, 175, 200], el: 'fire', shout: '風起，縱火焚其攻具！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    firewall(u, a, X) {
      const wind = this.wind && this.wind.on > 0, sp = spMul(u);
      const p = this.shootProj(u, { dir: a.dir, speed: X.speed, range: this.skillRange(a.sk, a.lvl) * (wind ? 1.5 : 1), rad: X.w / 2 * (wind ? 2 : 1), shape: 'firewall', el: 'fire',
        onHit: (t: Unit) => {
          this.damage(u, t, X.dmg * sp * (t.structure ? 0.5 : 1), { magic: true, spell: true, stagger: 0.25, fire: true });
          if (t.alive && !t.structure) this.ignite(t, X.dmg * 0.1, 2.5, u);
          return false;
        } });
      p.wind = wind;
      if (wind) { // 東風：火牆經過的蘆葦整叢燒起來（同一叢 8 秒內只燒一次）
        const burn = () => {
          if (!this.projs.includes(p)) return;
          const br = SG.MAP.brushAt(p.x, p.y), bf = this.brushFire || (this.brushFire = {});
          if (br >= 0 && !(bf[br] > this.t)) { bf[br] = this.t + 8; this.zone(u, { x: p.x, y: p.y, r: 130, t: 4, tick: 0.5, dmg: X.dmg * 0.15 * sp, ground: true, fire: true, pal: 'fire', el: null, wild: 1 }); }
          this.later(0.1, burn);
        };
        burn();
      }
      if (wind) this.tell(u, '東風助火：火牆加寬、加長', 'info');
    },
    citywall(u, a, X) {
      const c = a.pt, d = a.dir, tx = -d[1], ty = d[0], h = X.len / 2; // 垂直於施放方向
      const seg = [c[0] - tx * h, c[1] - ty * h * K, c[0] + tx * h, c[1] + ty * h * K];
      this.addWall(seg, 36, X.dur, u.team, 'city');
      this.fx({ k: 'slam', x: c[0], y: c[1], col: '#d8a860' });
    },
    burnsiege(u, a, X) {
      const sp = spMul(u);
      this.zone(u, { x: a.pt[0], y: a.pt[1], r: X.r, t: X.dur, tick: 0.5, dmg: X.dps * 0.5 * sp, ground: true, fire: true, pal: 'fire', name: a.sk.name, x2: { burnSiege: X.dps * 2 * sp } });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 嚴刑：技能打低血量的人加傷
    hook('damage.before', 130, function (c) { const own0 = c.own, o = c.o, t = c.t; if (own0.sg && own0.sg.harsh && o && (o.spell || o.sk) && t.hp < t.maxHp * 0.4) c.a *= 1 + own0.sg.harsh.v('amp') / 100; }),
    // 焚械：區域內的攻城器、召喚物持續受傷
    hook('zone', 6, function (z, dt) { const e = z.x2; if (e.burnSiege) for (const t of this.units) if (t.alive && t.team !== z.team && (t.siege || t.summon) && gd(z.x, z.y, t.x, t.y) <= z.r) this.damage(z.owner, t, e.burnSiege * dt, { magic: true, quiet: true, fire: true }); }),
  ],
  // 電腦什麼時候放這招
  ai: {
    citywall: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 600 && (t!.hp / t!.maxHp < 0.5 || hpR < 0.4),
    burnsiege: ({ w, u, t, d, hero, hpR, foeHeroes }) => !!t && ((hero && d < 650) || (!!t.siege && d < 650)),
  },
});
